import { useViewport } from '@meterup/atto';
import { useLocalStorage } from '@meterup/common';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

interface DockContextData {
  minimized: boolean;
  setMinimized: (minimized: boolean) => void;
  menuOpen: boolean;
  setMenuOpen: (menuOpen: boolean) => void;
  flyoutOpen: boolean;
  alertsOpen: boolean;
  setAlertsOpen: (alertsOpen: boolean) => void;
  jobsOpen: boolean;
  setJobsOpen: (jobsOpen: boolean) => void;
}

const DockContext = createContext<DockContextData>({
  minimized: false,
  setMinimized: () => {},
  menuOpen: false,
  setMenuOpen: () => {},
  flyoutOpen: false,
  alertsOpen: false,
  setAlertsOpen: () => {},
  jobsOpen: false,
  setJobsOpen: () => {},
});

export function DockProvider({ children }: { children?: ReactNode }) {
  const { breakpoint } = useViewport();
  const [minimized, setMinimized] = useLocalStorage('dashboard.dock.minimized', false);
  const [menuOpen, setMenuOpenState] = useState(false);
  const [alertsOpen, setAlertsOpenState] = useState(false);
  const [jobsOpen, setJobsOpenState] = useState(false);

  const setMenuOpen = useCallback((value: boolean) => {
    setMenuOpenState(value);
    if (value) {
      setAlertsOpenState(false);
      setJobsOpenState(false);
    }
  }, []);

  const setAlertsOpen = useCallback((value: boolean) => {
    setAlertsOpenState(value);
    if (value) {
      setMenuOpenState(false);
      setJobsOpenState(false);
    }
  }, []);

  const setJobsOpen = useCallback((value: boolean) => {
    setJobsOpenState(value);
    if (value) {
      setMenuOpenState(false);
      setAlertsOpenState(false);
    }
  }, []);

  const flyoutOpen = alertsOpen || jobsOpen;

  const value = useMemo(() => {
    const minimizedValue = breakpoint === 'mobile' ? false : minimized;

    return {
      minimized: minimizedValue,
      setMinimized,
      menuOpen,
      setMenuOpen,
      flyoutOpen,
      alertsOpen,
      setAlertsOpen,
      jobsOpen,
      setJobsOpen,
    };
  }, [
    breakpoint,
    minimized,
    setMinimized,
    menuOpen,
    setMenuOpen,
    alertsOpen,
    setAlertsOpen,
    jobsOpen,
    setJobsOpen,
    flyoutOpen,
  ]);

  return <DockContext.Provider value={value}>{children}</DockContext.Provider>;
}

export function useDockContext() {
  return useContext(DockContext);
}
